import React from 'react';
import { Sun, Moon, Clock, Trophy, AlertTriangle, TrendingUp } from 'lucide-react';

export default function SessionMatrix({ sessionMatrix }) {
  if (!sessionMatrix || sessionMatrix.length === 0) return null;

  // Find best session by win rate
  const activeSessions = sessionMatrix.filter((s) => s.total_trades > 0);
  const bestSession = activeSessions.length > 0
    ? activeSessions.reduce((max, s) => (s.win_rate > max.win_rate ? s : max), activeSessions[0])
    : null;

  const SESSION_ICONS = {
    Asia: <Moon className="w-5 h-5 text-indigo-400" />,
    London: <Sun className="w-5 h-5 text-amber-400" />,
    'New York': <Clock className="w-5 h-5 text-emerald-400" />,
    'NY Overlap': <TrendingUp className="w-5 h-5 text-cyan-400" />,
  };

  const SESSION_HOURS = {
    Asia: '00:00 - 08:00 UTC',
    London: '07:00 - 15:00 UTC',
    'New York': '12:00 - 20:00 UTC',
    'NY Overlap': '12:00 - 15:00 UTC',
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-8 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            Trading Session Edge Matrix
          </h3>
          <p className="text-xs text-slate-400">
            Performance breakdown across market sessions to identify where your ICC edge is strongest
          </p>
        </div>

        {bestSession && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
            <span>Highest Edge: <strong>{bestSession.session}</strong> ({bestSession.win_rate}%)</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {sessionMatrix.map((item) => {
          const isTop = bestSession && bestSession.session === item.session && item.total_trades > 0;
          return (
            <div
              key={item.session}
              className={`p-4 rounded-xl border transition-all ${
                isTop
                  ? 'bg-gradient-to-b from-slate-900 via-slate-900 to-emerald-950/40 border-emerald-500/50 shadow-md shadow-emerald-950/20'
                  : 'bg-slate-950/60 border-slate-800/80 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {SESSION_ICONS[item.session] || <Clock className="w-4 h-4 text-cyan-400" />}
                  <h4 className="font-bold text-sm text-white">{item.session}</h4>
                </div>
                {isTop && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider bg-emerald-500 text-slate-950">
                    TOP EDGE
                  </span>
                )}
              </div>

              <p className="text-[11px] text-slate-500 mb-3 font-mono">
                {SESSION_HOURS[item.session] || ''}
              </p>

              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Win Rate</span>
                  <span className={`font-bold font-mono ${item.win_rate >= 60 ? 'text-emerald-400' : item.win_rate >= 40 ? 'text-yellow-400' : 'text-slate-300'}`}>
                    {item.win_rate}%
                  </span>
                </div>

                {/* Progress bar for Win Rate */}
                <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      item.win_rate >= 60 ? 'bg-emerald-500' : item.win_rate >= 40 ? 'bg-yellow-500' : 'bg-slate-600'
                    }`}
                    style={{ width: `${Math.min(100, item.win_rate)}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs pt-1">
                  <span className="text-slate-400">Avg R:R</span>
                  <span className="font-mono text-cyan-400 font-semibold">{item.avg_rr}R</span>
                </div>

                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Net R-Multiple</span>
                  <span className={`font-mono font-bold ${item.net_r >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {item.net_r >= 0 ? `+${item.net_r}` : item.net_r}R
                  </span>
                </div>

                <div className="flex justify-between text-xs text-slate-500 pt-1 border-t border-slate-800/60">
                  <span>Trades Logged</span>
                  <span className="font-mono text-slate-300">{item.total_trades}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
